/**
 * ForRecruitersPage — the public /for/recruiters archetype landing page.
 *
 * Ported from the locked design design/mockups/V1_Landing_For_Recruiters_v1.html
 * (Template 2 instance, source: V1_Archetype_Landing_Pages.md Page 1).
 *
 * The app shell's <TopNav> renders the nav globally; this page is the
 * mockup content below <nav class="marketing-nav"> plus the canonical
 * footer. Scoped <style> block (media queries can't live in inline
 * styles); colors alias onto locked --ppr-* tokens. Unique class prefix
 * ppr-recruiters- so the global <style> never collides with sibling
 * marketing pages.
 *
 * Indexable page — usePageMeta only, no useNoindex.
 */

import { Link } from "react-router-dom";
import { usePageMeta } from "../../lib/use-page-meta.js";

const RECRUITERS_DESCRIPTION =
  "PeoplePrints gives recruiters a tactical read on every candidate before the screen — no assessment, no test, no candidate friction. From $49/month.";

const FAQS = [
  {
    q: "Does the candidate have to take a test or assessment?",
    a: "No. PeoplePrints generates the Profile from minimal common data points you already have. The candidate does nothing and never has to know a Profile was generated.",
  },
  {
    q: "How is this different from Crystal Knows or a LinkedIn-based tool?",
    a: "Tools that scrape a public profile need the candidate to have a discoverable LinkedIn footprint — many strong candidates don't. PeoplePrints works from minimal common data points, so it produces a read on every candidate, not just the ones with a public presence.",
  },
  {
    q: "Is this a hire / no-hire score?",
    a: "No. PeoplePrints tells you how to interview the candidate. It does not tell you whether to hire them, and it does not produce an applicant score. EEOC-compliant evaluation stays with you.",
  },
  {
    q: "How fast is it?",
    a: "The Profile generates instantly. There is no assessment cycle to wait on — you can prepare for an interview that was scheduled an hour ago.",
  },
  {
    q: "What does it cost?",
    a: "The Recruiter tier is $49/month for up to 25 Profiles per month. There is no per-candidate fee.",
  },
];

// FAQPage structured data — derived from FAQS so the schema text stays
// verbatim-matched to the on-page FAQ (a Google requirement for FAQ rich
// results). Mirrors the locked pattern in PricingPage.jsx.
const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const FOOTER_LINKS = [
  { to: "/#how-it-works", label: "Product" },
  { to: "/pricing", label: "Pricing" },
  { to: "/blog", label: "Blog" },
  { to: "/for/recruiters", label: "For Recruiters" },
  { to: "/for/managers", label: "For Managers" },
  { to: "/for/hr-directors", label: "For HR Directors" },
  { to: "/for/founders", label: "For Founders" },
  { to: "/for/employee-relations", label: "For Employee Relations" },
  { to: "/methodology", label: "Methodology" },
  { to: "/about", label: "About" },
  { to: "/security-and-privacy", label: "Security & Privacy" },
  { to: "/demo", label: "Request a Demo" },
];

export default function ForRecruitersPage() {
  usePageMeta({
    title:
      "Read Every Candidate Before the Interview — PeoplePrints for Recruiters",
    description: RECRUITERS_DESCRIPTION,
    canonicalPath: "/for/recruiters",
  });

  return (
    <div className="ppr-recruiters-root">
      <style>{RECRUITERS_CSS}</style>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_JSONLD) }}
      />

      {/* ── BLOCK 2 — HERO ── */}
      <section className="ppr-recruiters-hero">
        <div className="ppr-recruiters-hero-inner">
          <p className="ppr-recruiters-hero-eyebrow">
            For recruiters &amp; talent acquisition
          </p>
          <h1 className="ppr-recruiters-hero-h1">
            Read every candidate before the screen — without asking them to
            take a test.
          </h1>
          <p className="ppr-recruiters-hero-sub">
            You run a real pipeline. Phone screens, technical rounds, the final
            loop — every candidate is a stranger you have minutes to read.
            PeoplePrints gives you a tactical read on each one before you ever
            get on the call, generated from data you already have, with zero
            friction added to the candidate experience.
          </p>
        </div>
      </section>

      {/* ── BLOCK 3 — EXTRACTABLE DEFINITION ── */}
      <section className="ppr-recruiters-block ppr-recruiters-compact">
        <div className="ppr-recruiters-block-inner">
          <div className="ppr-recruiters-definition">
            <p className="ppr-recruiters-def-label">
              What PeoplePrints does for recruiters
            </p>
            <p className="ppr-recruiters-def-text">
              PeoplePrints is a pre-interaction intelligence tool for
              recruiters. It generates a{" "}
              <strong>Predisposition Profile</strong> for each candidate from
              minimal common data points — no assessment, no test, no LinkedIn
              scrape required — and converts that Profile into an Interview
              Blueprint that tells the recruiter how the candidate tends to make
              decisions, what tends to motivate them, and how to structure the
              interview to surface real signal instead of rehearsed answers.
            </p>
          </div>
        </div>
      </section>

      {/* ── BLOCK 4 — THE PAIN ── */}
      <section className="ppr-recruiters-block ppr-recruiters-compact">
        <div className="ppr-recruiters-block-inner">
          <p className="ppr-recruiters-block-eyebrow">Sound familiar?</p>
          <h2 className="ppr-recruiters-block-h2">
            You have minutes to read a stranger who has practiced their answers.
          </h2>
          <p className="ppr-recruiters-pain-quote">
            You have a résumé, a recruiter-screen summary, maybe a LinkedIn
            page. You have minutes. The candidate has practiced their answers.
            The standard interview script asks the same five questions every
            senior candidate has already answered in every previous loop. You
            find out you misread someone after the offer, not before.
          </p>
        </div>
      </section>

      {/* ── BLOCK 5 — THE SHIFT ── */}
      <section className="ppr-recruiters-block ppr-recruiters-compact ppr-recruiters-tinted">
        <div className="ppr-recruiters-block-inner">
          <p className="ppr-recruiters-block-eyebrow">The shift</p>
          <h2 className="ppr-recruiters-block-h2">
            Walk into every screen with the read already done.
          </h2>
          <p className="ppr-recruiters-block-body">
            With PeoplePrints, you walk into every screen with the read already
            done. The Interview Blueprint tells you what to ask first, what to
            expect when the candidate is under pressure, what tends to land, and
            what tends to push them away. You stop running a generic script and
            start running the interview that actually fits the person.
          </p>
        </div>
      </section>

      {/* ── BLOCK 6 — WHAT YOU GET (In-This-Blueprint card grammar) ── */}
      <section className="ppr-recruiters-block ppr-recruiters-compact">
        <div className="ppr-recruiters-block-inner">
          <h2 className="ppr-recruiters-block-h2">
            The reads and Blueprints that matter to a recruiter.
          </h2>
          <div className="ppr-recruiters-itb-card">
            <div className="ppr-recruiters-itb-stripe" />
            <div className="ppr-recruiters-itb-inner">
              <p className="ppr-recruiters-itb-label">What you get</p>
              <ol className="ppr-recruiters-itb-list">
                <li>
                  <strong>
                    A Predisposition Profile for every candidate
                  </strong>{" "}
                  — generated from minimal common data points, no candidate
                  action required.
                </li>
                <li>
                  <strong>An Interview Blueprint per Profile</strong> — the
                  tactical sequence for this specific candidate.
                </li>
                <li>
                  <strong>Saved hiring funnels</strong> — organize Profiles by
                  req.
                </li>
                <li>
                  <strong>Email + browser-extension delivery</strong> — the
                  read where you already work (extension shipping V1.x).
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* ── BLOCK 7 — HOW IT WORKS ── */}
      <section className="ppr-recruiters-block ppr-recruiters-compact ppr-recruiters-tinted">
        <div className="ppr-recruiters-block-inner">
          <p className="ppr-recruiters-block-eyebrow">How it works</p>
          <h2 className="ppr-recruiters-block-h2">
            Minimal data in. Profile. Blueprint.
          </h2>
          <div className="ppr-recruiters-step-list">
            <div className="ppr-recruiters-step-row">
              <div className="ppr-recruiters-step-num">1</div>
              <p className="ppr-recruiters-step-text">
                <strong>Enter the candidate's common data points</strong> — the
                kind of data already on the application.
              </p>
            </div>
            <div className="ppr-recruiters-step-row">
              <div className="ppr-recruiters-step-num">2</div>
              <p className="ppr-recruiters-step-text">
                <strong>
                  The engine generates the Predisposition Profile
                </strong>{" "}
                — instantly, no assessment cycle.
              </p>
            </div>
            <div className="ppr-recruiters-step-row">
              <div className="ppr-recruiters-step-num">3</div>
              <p className="ppr-recruiters-step-text">
                <strong>Open the Interview Blueprint</strong> — and walk into
                the screen prepared.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── BLOCK 8 — PRICING POINTER ── */}
      <section className="ppr-recruiters-block ppr-recruiters-compact">
        <div className="ppr-recruiters-block-inner">
          <div className="ppr-recruiters-price-pointer">
            <p className="ppr-recruiters-pp-tier">Recruiter — $49 / month</p>
            <p className="ppr-recruiters-pp-body">
              The Recruiter tier is built for you: 25 Predisposition Profiles
              per month, Interview Blueprints, saved funnels —{" "}
              <strong>$49/month</strong>. Need reads on more than candidates —
              on the people you'll manage after you hire them? The Manager tier
              is the next step up.
            </p>
          </div>
          <p className="ppr-recruiters-xlink">
            Hiring into a team you'll then lead? See{" "}
            <Link to="/for/managers">PeoplePrints for managers →</Link>
          </p>
        </div>
      </section>

      {/* ── BLOCK 9 — FAQ ── */}
      <section className="ppr-recruiters-block ppr-recruiters-compact ppr-recruiters-tinted">
        <div className="ppr-recruiters-block-inner">
          <p className="ppr-recruiters-block-eyebrow">FAQ</p>
          <h2 className="ppr-recruiters-block-h2">Questions recruiters ask.</h2>
          <div className="ppr-recruiters-faq-list">
            {FAQS.map((f) => (
              <div className="ppr-recruiters-faq-item" key={f.q}>
                <h3 className="ppr-recruiters-faq-q">{f.q}</h3>
                <p className="ppr-recruiters-faq-a">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BLOCK 10 — CREDENTIALING STRIP ── */}
      <section className="ppr-recruiters-cred-strip">
        <div className="ppr-recruiters-cred-strip-inner">
          <span className="ppr-recruiters-cs-label">The engine</span>
          PeoplePrints is built on a proprietary behavioral-prediction engine.
          The full methodology is at{" "}
          <Link to="/methodology">/methodology</Link>.
        </div>
      </section>

      {/* ── BLOCK 11 — CTA ── */}
      <section className="ppr-recruiters-cta-band">
        <div className="ppr-recruiters-cta-band-inner">
          <h2 className="ppr-recruiters-cta-band-h">
            Read every candidate before the screen.
          </h2>
          <div className="ppr-recruiters-cta-actions">
            <Link
              to="/demo"
              className="ppr-recruiters-cta-btn ppr-recruiters-cta-primary"
            >
              Request a demo
            </Link>
            <Link
              to="/demo"
              className="ppr-recruiters-cta-btn ppr-recruiters-cta-secondary"
            >
              Book a 30-minute demo
            </Link>
          </div>
        </div>
      </section>

      {/* ── FOOTER — canonical site-wide footer ── */}
      <footer className="ppr-recruiters-footer">
        <div className="ppr-recruiters-footer-inner">
          <div className="ppr-recruiters-footer-links">
            {FOOTER_LINKS.map((l) => (
              <Link to={l.to} key={l.to}>
                {l.label}
              </Link>
            ))}
          </div>
          <div className="ppr-recruiters-footer-legal">
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </div>
          <p className="ppr-recruiters-footer-copy">
            © 2026 PeoplePrints™. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

const RECRUITERS_CSS = `
.ppr-recruiters-root {
  --c-navy: var(--ppr-color-accent-navy);
  --c-steel: var(--ppr-color-accent-steel);
  --c-steel-dark: var(--ppr-color-accent-steel-dark);
  --c-powder: var(--ppr-color-accent-powder);
  --c-pale: var(--ppr-color-accent-pale);
  --c-pale-soft: var(--ppr-color-accent-pale-soft);
  --c-coral: var(--ppr-color-accent-coral);
  --c-coral-dark: var(--ppr-color-accent-coral-dark);
  --c-coral-very-soft: var(--ppr-color-accent-coral-very-soft);
  /* --c-gold: the locked marketing mockups use a gold hero-eyebrow accent
     with no token in design-tokens.v2.json — ported as a literal, flagged
     for design. */
  --c-gold: #C99A4B;
  --c-surface: var(--ppr-color-background-surface);
  --c-text: var(--ppr-color-text-primary);
  --c-text-2: var(--ppr-color-text-secondary);
  --c-muted: var(--ppr-color-text-muted);
  --c-border: var(--ppr-color-border-default);
  --f-display: var(--ppr-typography-font-family-display);
  --f-body: var(--ppr-typography-font-family-body);
  --f-ui: var(--ppr-typography-font-family-ui);
  --f-mono: var(--ppr-typography-font-family-mono);
  font-family: var(--f-body);
  color: var(--c-text);
  line-height: 1.55;
  background: var(--c-surface);
}
.ppr-recruiters-root * { box-sizing: border-box; }

.ppr-recruiters-block { padding: 64px 32px; }
.ppr-recruiters-block.ppr-recruiters-compact { padding: 44px 32px; }
.ppr-recruiters-block.ppr-recruiters-tinted { background: var(--c-pale-soft); }
.ppr-recruiters-block-inner { max-width: 940px; margin: 0 auto; }
.ppr-recruiters-block-eyebrow { font-family: var(--f-display); font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--c-coral); margin: 0 0 14px; }
.ppr-recruiters-block-h2 { font-family: var(--f-display); font-size: 27px; line-height: 1.14; letter-spacing: -0.02em; color: var(--c-navy); margin: 0 0 16px; }
.ppr-recruiters-block-body { font-family: var(--f-body); font-size: 16.5px; line-height: 1.65; color: var(--c-text); margin: 0 0 16px; }
.ppr-recruiters-block-body strong { color: var(--c-navy); }

.ppr-recruiters-hero { padding: 80px 32px 56px; background: linear-gradient(180deg, var(--c-pale-soft) 0%, var(--c-surface) 100%); }
.ppr-recruiters-hero-inner { max-width: 900px; margin: 0 auto; text-align: center; }
.ppr-recruiters-hero-eyebrow { font-family: var(--f-ui); font-size: 12px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: var(--c-gold); margin: 0 0 18px; }
.ppr-recruiters-hero-h1 { font-family: var(--f-display); font-size: 40px; line-height: 1.1; letter-spacing: -0.025em; color: var(--c-navy); margin: 0 0 20px; }
.ppr-recruiters-hero-sub { font-family: var(--f-body); font-size: 18px; line-height: 1.65; color: var(--c-text-2); margin: 0 auto; max-width: 720px; text-align: left; }

.ppr-recruiters-definition { background: var(--c-pale-soft); border: 1.5px solid var(--c-powder); border-left: 6px solid var(--c-navy); padding: 26px 30px; }
.ppr-recruiters-def-label { font-family: var(--f-ui); font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--c-steel-dark); margin: 0 0 10px; }
.ppr-recruiters-def-text { font-family: var(--f-body); font-size: 17px; line-height: 1.65; color: var(--c-text); margin: 0; }
.ppr-recruiters-def-text strong { color: var(--c-navy); }

.ppr-recruiters-pain-quote { font-family: var(--f-body); font-size: 17px; font-style: italic; line-height: 1.6; color: var(--c-text); border-left: 4px solid var(--c-coral); background: var(--c-coral-very-soft); padding: 20px 24px; margin: 0; }

.ppr-recruiters-itb-card { background: var(--c-surface); border: 3px solid var(--c-navy); margin: 6px 0 0; }
.ppr-recruiters-itb-stripe { height: 8px; background: var(--c-coral); }
.ppr-recruiters-itb-inner { padding: 24px 28px; }
.ppr-recruiters-itb-label { font-family: var(--f-display); font-size: 19px; color: var(--c-navy); text-transform: uppercase; letter-spacing: 0.04em; margin: 0 0 14px; }
.ppr-recruiters-itb-list { margin: 0; padding: 0; list-style: none; counter-reset: itb; }
.ppr-recruiters-itb-list li { position: relative; padding: 11px 0 11px 38px; font-family: var(--f-body); font-size: 15.5px; line-height: 1.55; border-bottom: 1px solid var(--c-border); counter-increment: itb; }
.ppr-recruiters-itb-list li:last-child { border-bottom: none; padding-bottom: 0; }
.ppr-recruiters-itb-list li::before { content: counter(itb); position: absolute; left: 0; top: 10px; width: 23px; height: 23px; background: var(--c-navy); color: #FFF; border-radius: 50%; font-family: var(--f-display); font-size: 11px; display: flex; align-items: center; justify-content: center; }
.ppr-recruiters-itb-list strong { color: var(--c-navy); }

.ppr-recruiters-step-list { margin: 20px 0 0; }
.ppr-recruiters-step-row { display: grid; grid-template-columns: 40px 1fr; gap: 18px; padding: 15px 0; border-bottom: 1px solid var(--c-border); }
.ppr-recruiters-step-row:last-child { border-bottom: none; }
.ppr-recruiters-step-num { width: 32px; height: 32px; border-radius: 50%; background: var(--c-navy); color: #FFF; font-family: var(--f-display); font-size: 13px; display: flex; align-items: center; justify-content: center; }
.ppr-recruiters-step-text { font-family: var(--f-body); font-size: 15.5px; line-height: 1.6; color: var(--c-text); margin: 4px 0 0; }
.ppr-recruiters-step-text strong { color: var(--c-navy); }

.ppr-recruiters-price-pointer { border: 2px solid var(--c-navy); border-top: 8px solid var(--c-coral); background: var(--c-surface); padding: 26px 30px; }
.ppr-recruiters-pp-tier { font-family: var(--f-display); font-size: 13px; letter-spacing: 0.06em; text-transform: uppercase; color: var(--c-coral); margin: 0 0 8px; }
.ppr-recruiters-pp-body { font-family: var(--f-body); font-size: 16px; line-height: 1.6; color: var(--c-text); margin: 0; }
.ppr-recruiters-pp-body strong { color: var(--c-navy); }

.ppr-recruiters-xlink { font-family: var(--f-ui); font-size: 14px; color: var(--c-text-2); margin: 18px 0 0; }
.ppr-recruiters-xlink a { color: var(--c-coral-dark); font-weight: 600; text-decoration: none; }

.ppr-recruiters-faq-list { margin: 20px 0 0; }
.ppr-recruiters-faq-item { border: 1.5px solid var(--c-border); border-left: 4px solid var(--c-steel); margin: 0 0 12px; background: var(--c-surface); padding: 20px 24px; }
.ppr-recruiters-faq-q { font-family: var(--f-display); font-size: 15.5px; color: var(--c-navy); margin: 0 0 8px; letter-spacing: -0.005em; }
.ppr-recruiters-faq-a { font-family: var(--f-body); font-size: 15px; line-height: 1.6; color: var(--c-text); margin: 0; }
.ppr-recruiters-faq-a strong { color: var(--c-navy); }

.ppr-recruiters-cred-strip { background: var(--c-navy); color: #FFF; padding: 28px 32px; }
.ppr-recruiters-cred-strip-inner { max-width: 940px; margin: 0 auto; font-family: var(--f-body); font-size: 14.5px; line-height: 1.65; }
.ppr-recruiters-cred-strip-inner a { color: var(--c-powder); text-decoration: underline; }
.ppr-recruiters-cs-label { font-family: var(--f-display); font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--c-powder); display: block; margin: 0 0 8px; }

.ppr-recruiters-cta-band { background: var(--c-pale-soft); padding: 60px 32px; text-align: center; }
.ppr-recruiters-cta-band-inner { max-width: 760px; margin: 0 auto; }
.ppr-recruiters-cta-band-h { font-family: var(--f-display); font-size: 24px; line-height: 1.14; letter-spacing: -0.02em; color: var(--c-navy); margin: 0 0 24px; }
.ppr-recruiters-cta-actions { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; }
.ppr-recruiters-cta-btn { display: inline-block; padding: 14px 26px; border-radius: 6px; text-decoration: none; font-family: var(--f-display); font-size: 12.5px; letter-spacing: 0.06em; text-transform: uppercase; }
.ppr-recruiters-cta-primary { background: var(--c-coral); color: #FFF; border: 2px solid var(--c-coral); }
.ppr-recruiters-cta-secondary { background: transparent; color: var(--c-navy); border: 2px solid var(--c-navy); }

.ppr-recruiters-footer { background: var(--c-surface); border-top: 1px solid var(--c-border); padding: 44px 32px; }
.ppr-recruiters-footer-inner { max-width: 1080px; margin: 0 auto; text-align: center; }
.ppr-recruiters-footer-links { display: flex; gap: 20px; justify-content: center; flex-wrap: wrap; margin: 0 0 14px; font-family: var(--f-ui); font-size: 13px; font-weight: 600; }
.ppr-recruiters-footer-links a { color: var(--c-text-2); text-decoration: none; }
.ppr-recruiters-footer-legal { display: flex; gap: 16px; justify-content: center; margin: 0 0 16px; font-family: var(--f-ui); font-size: 12.5px; }
.ppr-recruiters-footer-legal a { color: var(--c-muted); text-decoration: none; }
.ppr-recruiters-footer-copy { font-family: var(--f-mono); font-size: 11.5px; color: var(--c-muted); }

@media (max-width: 800px) {
  .ppr-recruiters-hero-h1 { font-size: 30px; }
  .ppr-recruiters-block-h2 { font-size: 22px; }
}
`;
